import _ from 'lodash'

import { d } from 'core/service/lib/decimal'
import { roundDown, round } from 'core/service/lib/number-format'
import {
  findListRiderPlanCodeSelectedRiderPlanGroup,
  findSelectedRiderPlanGroup,
} from 'core/service/rider/premium-rate'
import { getToggles } from 'quick-quote/feature-toggles'
import VALUES from 'core/data-model/constants/values'

const PRECISION = 2

export const getRiderDiscount = () => {
  return require('core/service/data-config/rider-discount/discount.json')
}

export const getPLRDiscount = (riderDependencies) => {
  let discountRate = 0
  if (riderDependencies.occupationFactor === 0) {
    let sumAssured = riderDependencies.riderSumAssured
    let riderDiscount = getRiderDiscount()

    for (let item of riderDiscount.discountTable) {
      if (item.riderCode === 'PLR' || item.riderCode === 'PLS') {
        for (let rule of item.discountRules) {
          if (sumAssured >= rule.minSA && sumAssured <= rule.maxSA) {
            discountRate = rule.dicountRate
            break
          }
        }
      }
    }
  }
  return discountRate
}

export const calculateRiderPremiumForVaryingSumAssured = (riderDependencies, rider) => {
  const premiumRate = d(riderDependencies.riderPremiumRate)
  const sumAssured = d(riderDependencies.riderSumAssured)
  const modelFactor = d(riderDependencies.modelFactor)

  const premium = premiumRate
    .times(sumAssured)
    .dividedBy(1000)
    .times(modelFactor)
    .toNumber()
  return returnRiderPremiumWithDiscountFormat(roundDown(premium, PRECISION), rider, riderDependencies)
}

export const calculateRiderPremiumForVaryingSumAssuredRoundUp = (riderDependencies) => {
  const premiumRate = d(riderDependencies.riderPremiumRate)
  const sumAssured = d(riderDependencies.riderSumAssured)
  const modelFactor = d(riderDependencies.modelFactor)

  const premium = d(
    premiumRate
      .times(sumAssured)
      .dividedBy(1000)
      .toFixed(2)
  )
    .times(modelFactor)
    .toNumber()
  return roundDown(premium, PRECISION)
}

export const calculateRiderPremiumForPBG = (riderDependencies) => {
  const premiumRate = d(riderDependencies.riderPremiumRate)
  const sumAssured = d(riderDependencies.riderSumAssured)
  const modelFactor = d(riderDependencies.modelFactor)
  const premium = d(
    premiumRate
      .times(sumAssured)
      .floor()
      .dividedBy(100)
      .toFixed(2)
  )
    .times(modelFactor)
    .toNumber()
  return roundDown(premium, PRECISION)
}

export const calculateRiderPremiumWithVaryingSumAssuredAndDiscount = (riderDependencies, rider) => {
  const premiumRate = d(riderDependencies.riderPremiumRate)
  const sumAssured = d(riderDependencies.riderSumAssured)
  const modelFactor = d(riderDependencies.modelFactor)
  const occupationFactor = d(riderDependencies.occupationFactor)
  const discount = d(getPLRDiscount(riderDependencies))

  const premium = premiumRate
    .plus(occupationFactor)
    .minus(discount)
    .mul(sumAssured)
    .div(1000)
    .mul(modelFactor)
  return returnRiderPremiumWithDiscountFormat(roundDown(premium, PRECISION), rider, riderDependencies)
}

export const calculateRiderPremiumForHIC = (riderDependencies, rider) => {
  const annualPremium = d(riderDependencies.riderPremiumRate)
  const modelFactor = d(riderDependencies.modelFactor)
  const premium = annualPremium.times(modelFactor).toNumber()

  return returnRiderPremiumWithDiscountFormat(roundDown(premium, PRECISION), rider, riderDependencies)
}

export const calculateRiderPremiumForMEAPlus = calculateRiderPremiumForHIC
export const calculateRiderPremiumForMEB = calculateRiderPremiumForHIC
export const calculateRiderPremiumForMEK = calculateRiderPremiumForHIC
export const calculateRiderPremiumForMEX = calculateRiderPremiumForHIC

export const calculateRiderPremiumForHBUDR = (riderDependencies) => {
  const premiumRate = d(riderDependencies.riderPremiumRate)
  const modelFactor = d(riderDependencies.modelFactor)
  const premium = premiumRate.times(modelFactor).toNumber()

  return roundDown(premium, PRECISION)
}

export const calculateRiderPremiumForHSUDR = (riderDependencies) => {
  const premiumRate = d(riderDependencies.riderPremiumRate)
  const modelFactor = d(riderDependencies.modelFactor)
  const premium = premiumRate.times(modelFactor).toNumber()

  return roundDown(premium, PRECISION)
}

export const calculateRiderPremiumForCIUDR = (riderDependencies) => {
  const premiumRate = d(riderDependencies.riderPremiumRate)
  const sumAssured = d(riderDependencies.riderSumAssured)
  const modelFactor = d(riderDependencies.modelFactor)
  const premium = sumAssured
    .div(1000)
    .mul(premiumRate)
    .mul(modelFactor)
  return roundDown(premium, PRECISION)
}

export const calculateRiderPremiumForTPDUDR = calculateRiderPremiumForCIUDR

export const calculateRiderPremiumBasedOnBasicPremium = (riderDependencies) => {
  const { basicPremium, riderPremiumRate } = riderDependencies
  const premium = d(basicPremium)
    .div(d(100))
    .times(d(riderPremiumRate))

  return roundDown(premium, PRECISION)
}

export const calculateRiderPremiumFromPremiumRateAndModelFactor = (riderDependencies, rider) => {
  const { riderPremiumRate, modelFactor } = riderDependencies

  const premium = d(riderPremiumRate)
    .times(d(modelFactor))
    .toNumber()

  return returnRiderPremiumWithDiscountFormat(roundDown(premium, PRECISION), rider, riderDependencies)
}

export const calculateRiderPremiumForMEBG = calculateRiderPremiumFromPremiumRateAndModelFactor
export const calculateRiderPremiumForMESG = calculateRiderPremiumFromPremiumRateAndModelFactor
export const calculateRiderPremiumForESCI = calculateRiderPremiumFromPremiumRateAndModelFactor
export const calculateRiderPremiumForWPEWL85 = calculateRiderPremiumFromPremiumRateAndModelFactor
export const calculateRiderPremiumForWPEIP85 = calculateRiderPremiumFromPremiumRateAndModelFactor

export const calculateRiderPremiumForWPD = calculateRiderPremiumBasedOnBasicPremium
export const calculateRiderPremiumForWPDD = calculateRiderPremiumBasedOnBasicPremium

export const calculateRiderPremiumForWPTPD = (riderDependencies, rider) => {
  const riderPremiumRate = d(riderDependencies.riderPremiumRate)
  const occupationFactor = d(riderDependencies.occupationFactor)
  const modelFactor = d(riderDependencies.modelFactor)
  const sumAssured = d(riderDependencies.riderSumAssured)

  const premiumOccupation = d(round(riderPremiumRate.times(occupationFactor), PRECISION))
  const roundSumAssured = d(roundDown(sumAssured.div(d(100)), 3))
  const annualPremium = d(roundDown(roundSumAssured.times(premiumOccupation), PRECISION))
  return returnRiderPremiumWithDiscountFormat(
    roundDown(annualPremium.times(modelFactor), PRECISION),
    rider,
    riderDependencies
  )
}

export const calculateRiderPremiumForWPTPDCI = calculateRiderPremiumForWPTPD

export const calculateRiderPremiumForPBSDD = (riderDependencies, rider) => {
  const riderPremiumRate = d(riderDependencies.riderPremiumRate)
  const payerOccupationFactor = d(1)
  const modelFactor = d(riderDependencies.modelFactor)
  const sumAssured = d(riderDependencies.riderSumAssured)

  return returnRiderPremiumWithDiscountFormat(
    roundDown(
      riderPremiumRate
        .times(payerOccupationFactor)
        .div(d(100))
        .times(sumAssured)
        .times(modelFactor),
      PRECISION
    ),
    rider,
    riderDependencies
  )
}

export const calculateRiderPremiumForPBSDDCI = calculateRiderPremiumForPBSDD
export const calculateRiderPremiumForPBPDD = calculateRiderPremiumForPBSDD
export const calculateRiderPremiumForPBPDDCI = calculateRiderPremiumForPBSDD

export const calculateRiderPremiumForWP = (riderDependencies) => {
  const isUseReCalculateWP = getToggles().ENABLE_RECALCULATE_PREMIUM_WP

  const premiumRate = d(riderDependencies.riderPremiumRate)
  const occupationFactor = d(riderDependencies.occupationFactor)
  const sumAssured = d(riderDependencies.riderSumAssured)
  const modelFactor = d(riderDependencies.modelFactor)

  if (isUseReCalculateWP) {
    const premiumOccupation = round(premiumRate.times(occupationFactor), PRECISION)
    const premiumSumassured = round(
      d(premiumOccupation)
        .times(sumAssured)
        .dividedBy(1000),
      PRECISION
    )
    const premiumAmount = d(premiumSumassured).times(modelFactor)

    return roundDown(premiumAmount, PRECISION)
  } else {
    const premium = premiumRate
      .times(occupationFactor)
      .times(sumAssured)
      .dividedBy(1000)
      .times(modelFactor)
      .toNumber()

    return roundDown(premium, PRECISION)
  }
}

export const calculateRiderPremiumForDCI = (riderDependencies, rider) => {
  const premium = d(riderDependencies.riderPremiumRate)
    .plus(d(riderDependencies.occupationFactor))
    .dividedBy(1000)
    .times(d(riderDependencies.riderSumAssured).times(d(riderDependencies.modelFactor)))
    .toNumber()
  return returnRiderPremiumWithDiscountFormat(roundDown(premium, PRECISION), rider, riderDependencies)
}

export const calculateRiderPremiumForCIES = (riderDependencies, rider) => {
  const { summaryRiderPremium } = calculateStructureRiderPremiumForCIES(riderDependencies, rider)
  return returnRiderPremiumWithDiscountFormat(summaryRiderPremium, rider, riderDependencies)
}

export const calculateRiderSelectedPlanGroupForCIES = (riderDependencies, rider) => {
  const { selectedPlanGroup } = calculateStructureRiderPremiumForCIES(riderDependencies, rider)
  return selectedPlanGroup
}

export const calculateStructureRiderPremiumForCIES = (riderDependencies, rider) => {
  const riderPlanGroups = findSelectedRiderPlanGroup(rider)
  const riderPlanCodes = findListRiderPlanCodeSelectedRiderPlanGroup(rider)

  let summaryRiderPremium = 0
  let selectedPlanGroup = {}
  for (let riderCode of riderPlanCodes) {
    const riderPlan = riderPlanGroups.planGroups.find((rider) => rider.code === riderCode)
    const sumAssuredWithRatio = d(riderDependencies.riderSumAssured)
      .times(d(riderPlan.ratioSA))
      .dividedBy(d(100))
      .toNumber()

    const riderSumAssured = riderPlan.maxValueSA
      ? Math.min(sumAssuredWithRatio, riderPlan.maxValueSA)
      : sumAssuredWithRatio

    const riderPremiumRate = riderDependencies.riderPremiumRates
      ? riderDependencies.riderPremiumRates[riderCode]
      : riderDependencies.riderPremiumRate

    const step1 = roundDown(d(riderSumAssured).dividedBy(1000), 3)
    const step2 = roundDown(d(riderPremiumRate).times(d(step1)), PRECISION)
    const riderPremium = roundDown(d(step2).times(d(riderDependencies.modelFactor)), PRECISION)
    summaryRiderPremium += riderPremium
    selectedPlanGroup[riderCode] = {
      riderCode: riderCode,
      riderSumAssured: riderSumAssured,
      riderPremium: riderPremium,
      description: riderPlan.description ? riderPlan.description : '',
      descriptionDisplay: riderPlan.descriptionDisplay ? riderPlan.descriptionDisplay : '',
      group: riderPlan.group ? riderPlan.group : '',
    }
  }
  return { summaryRiderPremium: round(summaryRiderPremium, 2), selectedPlanGroup }
}

export const calculateRiderPremiumSelectedPlanGroup = (
  riderPremiumRates,
  rider,
  riderOccupationFactors,
  basicPremium,
  modelFactor,
  modelFactorPeriod,
  riderPayerOccupationFactors,
  loan,
  userAgentType
) => {
  const riderPremiumRate = riderPremiumRates[rider.code]
  const riderSumAssured = rider.sumAssured
  const occupationFactor = riderOccupationFactors[rider.code]
  const payerOccupationFactor = riderPayerOccupationFactors[rider.code]
  const allDependencies = {
    riderPremiumRate,
    occupationFactor,
    riderSumAssured,
    basicPremium: basicPremium.value ?? 0,
    modelFactor,
    modelFactorPeriod,
    payerOccupationFactor,
    loan,
    userAgentType,
    riderPremiumRates,
  }

  const findCalculateRiderSelectedPlanGroup = (rider) => {
    switch (rider.code) {
      case VALUES.CIES:
        return calculateRiderSelectedPlanGroupForCIES(allDependencies, rider)
      default:
        return {}
    }
  }

  const selectedPlanGroup = findCalculateRiderSelectedPlanGroup(rider)
  return selectedPlanGroup
}

export const calculateRiderPremiumForMEH = (riderDependencies) => {
  const premiumRate = d(riderDependencies.riderPremiumRate)
  const modelFactor = d(riderDependencies.modelFactor)

  const premium = premiumRate.times(modelFactor).toNumber()

  return roundDown(premium, PRECISION)
}

export const calculateRiderPremiumForFH = (riderDependencies) => {
  const premiumRate = d(riderDependencies.riderPremiumRate)
  const modelFactor = d(riderDependencies.modelFactor)
  const occupationFactor = d(riderDependencies.occupationFactor)

  const premium = premiumRate
    .times(occupationFactor)
    .times(modelFactor)
    .toNumber()

  return roundDown(premium, PRECISION)
}

export const calculateRiderPremiumForMHP = (riderDependencies, rider) => {
  const premiumRate = d(riderDependencies.riderPremiumRate)
  const modelFactor = d(riderDependencies.modelFactor)
  const premium = premiumRate.times(modelFactor).toNumber()
  return returnRiderPremiumWithDiscountFormat(roundDown(premium, PRECISION), rider, riderDependencies)
}

export const calculateRiderPremiumForMCI = calculateRiderPremiumForMHP

export const calculateRiderPremiumForMRTAAP = (riderDependencies) => {
  const premiumRate = d(riderDependencies.riderPremiumRate)
  const sumAssured = d(riderDependencies.riderSumAssured)
  if (typeof riderDependencies.loan == 'undefined') {
    return 0
  }
  return riderDependencies.loan.coveragePlan.coverage.paymentMethod === VALUES.CASH_INCLUDE_LOAN
    ? round(premiumRate * (sumAssured / 1000), PRECISION)
    : round(sumAssured * (roundDown(premiumRate, 4) / 1000), PRECISION)
}

export const calculateRiderPremiumForPARIDER = (riderDependencies) => {
  const premiumRate = d(riderDependencies.riderPremiumRate).toNumber()
  return premiumRate
}

export const returnRiderPremiumWithDiscountFormat = (premium, rider, riderDependencies) => {
  const campaignDiscountValue = riderDependencies.isRiderCampaignDiscountActive
    ? _.get(rider, 'campaignDiscount.discount.value', 0)
    : 0

  const premiumDiscount = round(
    d(100)
      .minus(campaignDiscountValue)
      .dividedBy(100)
      .times(premium),
    PRECISION
  )

  return riderDependencies.isRiderCampaignDiscountActive ? { premium: premiumDiscount, fullPremium: premium } : premium
}
